import React, { useEffect, useMemo, useRef, useState } from "react";
import { Modal, Button, Spinner, Alert, Form } from "react-bootstrap";
import FlipClassService, {
  SessionGroupRecord,
  CaseDiscussionOverview,
  CaseDiscussionTile,
  CaseDiscussionTurn,
  WalkHereNext,
} from "../../_services/FlipClassService";
import CaseStudyService, { CaseStudy } from "../../_services/CaseStudyService";
import "./CaseDiscussionManager.css";

interface CaseDiscussionManagerProps {
  stepId: number;
  batchId: number;
  lessonPlanId?: number;
  stepName?: string;
  isOpen: boolean;
  onClose: () => void;
}

const OVERVIEW_POLL_MS = 4000;
const DETAIL_POLL_MS = 3000;

function todayISO(): string {
  return new Date().toISOString().split("T")[0]!;
}

const signalLabel: Record<string, string> = {
  on_track: "On track",
  stuck: "Stuck",
  off_topic: "Off topic",
  key_insight: "Key insight",
  misconception: "Misconception",
};

const signalClass: Record<string, string> = {
  on_track: "cdm-sig-ontrack",
  stuck: "cdm-sig-stuck",
  off_topic: "cdm-sig-offtopic",
  key_insight: "cdm-sig-insight",
  misconception: "cdm-sig-misconception",
};

const CaseDiscussionManager: React.FC<CaseDiscussionManagerProps> = ({
  stepId,
  batchId,
  lessonPlanId,
  stepName,
  isOpen,
  onClose,
}) => {
  const authorCaseUrl = (() => {
    const params = new URLSearchParams();
    params.set("from", "class-review");
    params.set("stepId", String(stepId));
    params.set("batchId", String(batchId));
    if (lessonPlanId) params.set("lessonPlanId", String(lessonPlanId));
    if (stepName) params.set("stepName", stepName);
    return `/case-studies?${params.toString()}`;
  })();
  const [phase, setPhase] = useState<"setup" | "live">("setup");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [groups, setGroups] = useState<SessionGroupRecord[]>([]);
  const [cases, setCases] = useState<CaseStudy[]>([]);
  const [stepCases, setStepCases] = useState<CaseStudy[]>([]);
  const [selectedCaseId, setSelectedCaseId] = useState<number | null>(null);

  const [overview, setOverview] = useState<CaseDiscussionOverview | null>(null);
  const [detailTile, setDetailTile] = useState<CaseDiscussionTile | null>(null);
  const [detailTurns, setDetailTurns] = useState<CaseDiscussionTurn[]>([]);
  const [injectDraft, setInjectDraft] = useState("");
  const [starting, setStarting] = useState(false);

  const overviewPollRef = useRef<NodeJS.Timeout | null>(null);
  const detailPollRef = useRef<NodeJS.Timeout | null>(null);
  const lastTurnIdxRef = useRef<number>(-1);
  const seenTurnIdsRef = useRef<Set<number>>(new Set());

  const mergeTurns = (incoming: CaseDiscussionTurn[]) => {
    if (incoming.length === 0) return;
    const fresh = incoming.filter((t) => !seenTurnIdsRef.current.has(t.Id));
    if (fresh.length === 0) return;
    fresh.forEach((t) => {
      seenTurnIdsRef.current.add(t.Id);
      if (t.TurnIdx > lastTurnIdxRef.current) lastTurnIdxRef.current = t.TurnIdx;
    });
    setDetailTurns((prev) => [...prev, ...fresh]);
  };

  useEffect(() => {
    if (!isOpen) {
      stopAllPolling();
      return;
    }
    loadSetup();
  }, [isOpen, stepId, batchId]);

  const stopAllPolling = () => {
    if (overviewPollRef.current) clearInterval(overviewPollRef.current);
    if (detailPollRef.current) clearInterval(detailPollRef.current);
    overviewPollRef.current = null;
    detailPollRef.current = null;
  };

  const loadSetup = async () => {
    try {
      setLoading(true);
      setError(null);
      const [groupsRes, stepCasesRes, allCasesRes, overviewRes] = await Promise.all([
        FlipClassService.listGroups(stepId, batchId),
        CaseStudyService.listForStep(stepId),
        CaseStudyService.list(),
        FlipClassService.getCaseDiscussionOverview(stepId, todayISO(), batchId),
      ]);
      if (groupsRes.success) setGroups(groupsRes.data.groups);
      if (stepCasesRes.success) setStepCases(stepCasesRes.data.cases);
      if (allCasesRes.success) setCases(allCasesRes.data.cases);
      if (overviewRes.success && overviewRes.data.groups.length > 0) {
        setOverview(overviewRes.data);
        setPhase("live");
        startOverviewPolling();
      } else {
        setPhase("setup");
      }
    } catch (e: any) {
      setError(e?.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  const startOverviewPolling = () => {
    if (overviewPollRef.current) return;
    overviewPollRef.current = setInterval(async () => {
      try {
        const res = await FlipClassService.getCaseDiscussionOverview(stepId, todayISO(), batchId);
        if (!res.success) return;
        setOverview(res.data);
        if (res.data.groups.length === 0) {
          if (overviewPollRef.current) {
            clearInterval(overviewPollRef.current);
            overviewPollRef.current = null;
          }
          setPhase("setup");
        }
      } catch {
        /* ignore */
      }
    }, OVERVIEW_POLL_MS);
  };

  const handleAttachCase = async (caseId: number) => {
    try {
      await CaseStudyService.attachToStep(stepId, caseId);
      const stepCasesRes = await CaseStudyService.listForStep(stepId);
      if (stepCasesRes.success) setStepCases(stepCasesRes.data.cases);
    } catch (e: any) {
      setError(e?.message || "Failed to attach case");
    }
  };

  // "Run as" setter — reuses attach (upsert) to set both axes on the attachment.
  const handleSetMode = async (
    caseId: number,
    runMode: "discussion" | "response",
    participation: "individual" | "group",
  ) => {
    try {
      await CaseStudyService.attachToStep(stepId, caseId, { runMode, participation });
      // Only a group discussion is startable from here; if this case is no longer
      // that, drop it from the selection.
      if (!(runMode === "discussion" && participation === "group") && selectedCaseId === caseId) {
        setSelectedCaseId(null);
      }
      const res = await CaseStudyService.listForStep(stepId);
      if (res.success) setStepCases(res.data.cases);
    } catch (e: any) {
      setError(e?.message || "Failed to update how this case runs");
    }
  };

  const handleStart = async () => {
    if (!selectedCaseId) {
      setError("Pick a case first.");
      return;
    }
    if (groups.length === 0) {
      setError("Form groups first (use the 'Form Groups' button).");
      return;
    }
    try {
      setStarting(true);
      setError(null);
      await Promise.all(
        groups.map((g) => FlipClassService.startCaseDiscussion(g.id, selectedCaseId))
      );
      const res = await FlipClassService.getCaseDiscussionOverview(stepId, todayISO(), batchId);
      if (res.success) setOverview(res.data);
      setPhase("live");
      startOverviewPolling();
    } catch (e: any) {
      setError(e?.message || "Failed to start discussions");
    } finally {
      setStarting(false);
    }
  };

  const openDetail = async (tile: CaseDiscussionTile) => {
    setDetailTile(tile);
    setDetailTurns([]);
    setInjectDraft("");
    lastTurnIdxRef.current = -1;
    seenTurnIdsRef.current = new Set();
    try {
      const res = await FlipClassService.getDiscussionTurns(tile.sessionId, -1);
      if (res.success) mergeTurns(res.data.turns);
    } catch (e: any) {
      setError(e?.message || "Failed to load conversation");
    }
    if (detailPollRef.current) clearInterval(detailPollRef.current);
    detailPollRef.current = setInterval(async () => {
      try {
        const res = await FlipClassService.getDiscussionTurns(
          tile.sessionId,
          lastTurnIdxRef.current
        );
        if (res.success) mergeTurns(res.data.turns);
      } catch {
        /* ignore */
      }
    }, DETAIL_POLL_MS);
  };

  const closeDetail = () => {
    if (detailPollRef.current) clearInterval(detailPollRef.current);
    detailPollRef.current = null;
    setDetailTile(null);
    setDetailTurns([]);
    setInjectDraft("");
    lastTurnIdxRef.current = -1;
    seenTurnIdsRef.current = new Set();
  };

  const handleInject = async () => {
    if (!detailTile || !injectDraft.trim()) return;
    try {
      await FlipClassService.teacherInject(detailTile.sessionId, injectDraft.trim());
      setInjectDraft("");
      const res = await FlipClassService.getDiscussionTurns(
        detailTile.sessionId,
        lastTurnIdxRef.current
      );
      if (res.success) mergeTurns(res.data.turns);
    } catch (e: any) {
      setError(e?.message || "Failed to send teacher message");
    }
  };

  const handleCloseAll = async () => {
    if (!overview) return;
    if (typeof window !== "undefined" && !window.confirm("Close all active discussions?")) return;
    try {
      await Promise.all(
        overview.groups.map((g) => FlipClassService.closeCaseDiscussion(g.sessionId))
      );
      const res = await FlipClassService.getCaseDiscussionOverview(stepId, todayISO(), batchId);
      if (res.success) setOverview(res.data);
    } catch (e: any) {
      setError(e?.message || "Failed to close discussions");
    }
  };

  const walkHereNext = overview?.walkHereNext || null;

  const availableCases = useMemo(() => {
    if (stepCases.length > 0) return stepCases;
    return cases;
  }, [stepCases, cases]);

  return (
    <Modal
      show={isOpen}
      onHide={() => {
        stopAllPolling();
        onClose();
      }}
      size="xl"
      dialogClassName="case-discussion-manager-dialog"
      backdrop="static"
    >
      <Modal.Header closeButton>
        <Modal.Title>
          AI Case Discussions
          <div className="cdm-subtitle">
            Socratic small-group discussions with AI tutor — and live teacher triage
          </div>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}

        {loading && (
          <div className="cdm-loading">
            <Spinner animation="border" /> Loading…
          </div>
        )}

        {!loading && phase === "setup" && (
          <div className="cdm-setup">
            <div className="cdm-prereq">
              <div className="cdm-prereq-card">
                <div className="cdm-prereq-num">1</div>
                <div className="cdm-prereq-content">
                  <strong>Groups</strong>
                  <div>
                    {groups.length > 0 ? (
                      <span className="cdm-prereq-ok">{groups.length} groups formed ✓</span>
                    ) : (
                      <span className="cdm-prereq-missing">
                        No groups yet — close this and use the "Form Groups" button first.
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="cdm-prereq-card">
                <div className="cdm-prereq-num">2</div>
                <div className="cdm-prereq-content">
                  <strong>Case</strong>
                  <div>
                    {availableCases.length > 0 ? (
                      <span className="cdm-prereq-ok">{availableCases.length} available</span>
                    ) : (
                      <span className="cdm-prereq-missing">
                        No cases in library —{" "}
                        <a href={authorCaseUrl}>
                          author one for this step →
                        </a>
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {availableCases.length > 0 && groups.length > 0 && (
              <div className="cdm-pick-case">
                <div className="cdm-pick-case-header">
                  <h4 className="cdm-section-title">Pick a case</h4>
                  <a className="cdm-author-new-link" href={authorCaseUrl}>
                    + Author new case
                  </a>
                </div>
                <div className="cdm-case-list">
                  {availableCases.map((cs) => {
                    const attached = !!stepCases.find((s) => s.Id === cs.Id);
                    const runMode = cs.RunMode || "discussion";
                    const participation = cs.Participation || "group";
                    // Only a group discussion is launched by the teacher here.
                    const selectable =
                      attached && runMode === "discussion" && participation === "group";
                    return (
                      <div
                        key={cs.Id}
                        className={`cdm-case-row ${selectedCaseId === cs.Id ? "is-selected" : ""}`}
                      >
                        <div className="cdm-case-select">
                          {selectable ? (
                            <input
                              type="radio"
                              name="case"
                              value={cs.Id}
                              checked={selectedCaseId === cs.Id}
                              onChange={() => setSelectedCaseId(cs.Id)}
                            />
                          ) : (
                            <span className="cdm-case-select-spacer" />
                          )}
                        </div>
                        <div className="cdm-case-info">
                          <strong>{cs.Title}</strong>
                          <div className="cdm-case-preview">
                            {cs.CaseText.slice(0, 160)}
                            {cs.CaseText.length > 160 ? "…" : ""}
                          </div>
                          {cs.KeyConcepts && cs.KeyConcepts.length > 0 && (
                            <div className="cdm-case-tags">
                              {cs.KeyConcepts.slice(0, 5).map((c, i) => (
                                <span key={i} className="cdm-case-tag">
                                  {c}
                                </span>
                              ))}
                            </div>
                          )}
                          {attached ? (
                            <>
                              <div className="cdm-runas">
                                <span className="cdm-runas-group">
                                  <span className="cdm-runas-label">Run as</span>
                                  <button
                                    type="button"
                                    className={runMode === "discussion" ? "active" : ""}
                                    onClick={() => handleSetMode(cs.Id, "discussion", participation)}
                                  >
                                    Discussion
                                  </button>
                                  <button
                                    type="button"
                                    className={runMode === "response" ? "active" : ""}
                                    onClick={() => handleSetMode(cs.Id, "response", participation)}
                                  >
                                    Response
                                  </button>
                                </span>
                                <span className="cdm-runas-group">
                                  <button
                                    type="button"
                                    className={participation === "individual" ? "active" : ""}
                                    onClick={() => handleSetMode(cs.Id, runMode, "individual")}
                                  >
                                    Individual
                                  </button>
                                  <button
                                    type="button"
                                    className={participation === "group" ? "active" : ""}
                                    onClick={() => handleSetMode(cs.Id, runMode, "group")}
                                  >
                                    Group
                                  </button>
                                </span>
                              </div>
                              {!selectable && (
                                <div className="cdm-mode-note">
                                  {runMode === "response"
                                    ? "Self-paced — students submit a written response (grade it under “Case Responses”)."
                                    : "Self-paced 1:1 — each student runs their own AI discussion."}
                                </div>
                              )}
                            </>
                          ) : (
                            <button
                              type="button"
                              className="cdm-attach-link"
                              onClick={() => handleAttachCase(cs.Id)}
                            >
                              Attach to step
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <Button
                  variant="primary"
                  size="lg"
                  onClick={handleStart}
                  disabled={!selectedCaseId || starting}
                  className="cdm-start-btn"
                >
                  {starting ? (
                    <>
                      <Spinner size="sm" animation="border" /> Starting…
                    </>
                  ) : (
                    <>
                      Start Discussion for {groups.length} Group{groups.length === 1 ? "" : "s"}
                    </>
                  )}
                </Button>
              </div>
            )}
          </div>
        )}

        {!loading && phase === "live" && overview && (
          <div className="cdm-live">
            {overview.groups.length > 0 && walkHereNext ? (
              <WalkHereNextCard walkHereNext={walkHereNext} overview={overview} onOpen={(gid) => {
                const tile = overview.groups.find((t) => t.groupId === gid);
                if (tile) openDetail(tile);
              }} />
            ) : overview.groups.length > 0 ? (
              <div className="cdm-all-good">
                <div className="cdm-all-good-icon">✨</div>
                <div>
                  <strong>All groups are on track.</strong>
                  <div className="cdm-all-good-sub">
                    No groups need you right now. Keep an eye on the tiles below.
                  </div>
                </div>
              </div>
            ) : null}

            {overview.groups.length > 0 && <AggregateStrip overview={overview} />}

            {overview.groups.length > 0 && (
              <div className="cdm-tiles">
                {overview.groups.map((tile) => (
                  <GroupTile key={tile.sessionId} tile={tile} onOpen={() => openDetail(tile)} />
                ))}
              </div>
            )}

            {overview.groups.length > 0 && (
              <div className="cdm-live-footer">
                <Button variant="danger" onClick={handleCloseAll}>
                  Close All Discussions
                </Button>
              </div>
            )}
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button
          variant="secondary"
          onClick={() => {
            stopAllPolling();
            onClose();
          }}
        >
          Close
        </Button>
      </Modal.Footer>

      {/* Detail modal — full conversation + inject */}
      <Modal
        show={!!detailTile}
        onHide={closeDetail}
        size="lg"
        dialogClassName="case-discussion-detail-dialog"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {detailTile?.groupName}
            <span className={`cdm-detail-signal ${signalClass[detailTile?.signal || "on_track"]}`}>
              {signalLabel[detailTile?.signal || "on_track"]}
            </span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {detailTile?.signalReason && (
            <div className="cdm-detail-reason">{detailTile.signalReason}</div>
          )}
          <div className="cdm-detail-thread">
            {detailTurns.length === 0 && (
              <div className="cdm-detail-empty">Loading conversation…</div>
            )}
            {detailTurns.map((t) => (
              <div key={t.Id} className={`cdm-detail-turn cdm-detail-turn-${t.ActorType}`}>
                <div className="cdm-detail-actor">
                  {t.ActorType === "ai" && "🤖 AI Tutor"}
                  {t.ActorType === "student" && "👤 Student"}
                  {t.ActorType === "teacher" && "👋 Teacher"}
                </div>
                <div className="cdm-detail-content">{t.Content}</div>
                {t.AIClassification && t.ActorType === "ai" && (
                  <div className="cdm-detail-ai-meta">
                    <span className={signalClass[t.AIClassification]}>
                      {signalLabel[t.AIClassification]}
                    </span>
                    {t.AIClassificationReason && <span> · {t.AIClassificationReason}</span>}
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="cdm-detail-inject">
            <Form.Control
              as="textarea"
              rows={2}
              placeholder="Inject a question or redirect for this group…"
              value={injectDraft}
              onChange={(e) => setInjectDraft(e.target.value)}
            />
            <Button
              variant="primary"
              onClick={handleInject}
              disabled={!injectDraft.trim()}
            >
              Send to group
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    </Modal>
  );
};

interface WalkHereNextCardProps {
  walkHereNext: WalkHereNext;
  overview: CaseDiscussionOverview;
  onOpen: (groupId: number) => void;
}

const WalkHereNextCard: React.FC<WalkHereNextCardProps> = ({ walkHereNext, onOpen }) => {
  return (
    <div className="cdm-walk-card">
      <div className="cdm-walk-header">
        <div className="cdm-walk-eyebrow">Walk here next →</div>
        <div className={`cdm-walk-signal ${signalClass[walkHereNext.signal]}`}>
          {signalLabel[walkHereNext.signal]}
        </div>
      </div>
      <div className="cdm-walk-group">{walkHereNext.groupName}</div>
      <div className="cdm-walk-reason">
        {walkHereNext.signalReason || "Needs your attention"}
        {walkHereNext.misconceptionTag && (
          <> · about <strong>{walkHereNext.misconceptionTag}</strong></>
        )}
        {" · "}
        {walkHereNext.minutesSinceActivity > 0 && (
          <span>{walkHereNext.minutesSinceActivity.toFixed(1)} min since last reply</span>
        )}
      </div>
      {walkHereNext.lastStudentResponse && (
        <div className="cdm-walk-quote">
          "{walkHereNext.lastStudentResponse}"
        </div>
      )}
      <div className="cdm-walk-probe">
        <span className="cdm-walk-probe-label">Suggested probe</span>
        <span className="cdm-walk-probe-text">{walkHereNext.suggestedProbe}</span>
      </div>
      <Button
        variant="light"
        className="cdm-walk-open"
        onClick={() => onOpen(walkHereNext.groupId)}
      >
        Open {walkHereNext.groupName} →
      </Button>
    </div>
  );
};

const AggregateStrip: React.FC<{ overview: CaseDiscussionOverview }> = ({ overview }) => {
  const { aggregate, misconceptionClusters } = overview;
  return (
    <div className="cdm-aggregate">
      <div className="cdm-agg-pills">
        <span className="cdm-agg-pill cdm-sig-ontrack">{aggregate.onTrack} on track</span>
        {aggregate.stuck > 0 && (
          <span className="cdm-agg-pill cdm-sig-stuck">{aggregate.stuck} stuck</span>
        )}
        {aggregate.offTopic > 0 && (
          <span className="cdm-agg-pill cdm-sig-offtopic">{aggregate.offTopic} off-topic</span>
        )}
        {aggregate.misconception > 0 && (
          <span className="cdm-agg-pill cdm-sig-misconception">
            {aggregate.misconception} misconception
          </span>
        )}
        {aggregate.keyInsight > 0 && (
          <span className="cdm-agg-pill cdm-sig-insight">
            {aggregate.keyInsight} key insight
          </span>
        )}
      </div>
      {misconceptionClusters.length > 0 && (
        <div className="cdm-clusters">
          <span className="cdm-clusters-label">Common confusions:</span>
          {misconceptionClusters.map((c) => (
            <span key={c.tag} className="cdm-cluster-pill">
              {c.tag} ({c.count})
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

const GroupTile: React.FC<{ tile: CaseDiscussionTile; onOpen: () => void }> = ({
  tile,
  onOpen,
}) => {
  return (
    <button type="button" className="cdm-tile" onClick={onOpen}>
      <div className="cdm-tile-header">
        <span className="cdm-tile-name">{tile.groupName}</span>
        <span className={`cdm-tile-signal ${signalClass[tile.signal]}`}>
          {signalLabel[tile.signal]}
        </span>
      </div>
      <div className="cdm-tile-meta">
        {tile.turnCount} turn{tile.turnCount === 1 ? "" : "s"} · {tile.minutesSinceActivity.toFixed(1)}m ago
      </div>
      {tile.lastAiQuestion && (
        <div className="cdm-tile-ai">
          <strong>AI:</strong> {tile.lastAiQuestion.slice(0, 120)}
          {tile.lastAiQuestion.length > 120 ? "…" : ""}
        </div>
      )}
      {tile.lastStudentResponse && (
        <div className="cdm-tile-student">
          <strong>Last reply:</strong> {tile.lastStudentResponse.slice(0, 100)}
          {tile.lastStudentResponse.length > 100 ? "…" : ""}
        </div>
      )}
    </button>
  );
};

export default CaseDiscussionManager;
